import React, { useState, useEffect } from "react";
import { InputGroup, InputRightElement } from "@chakra-ui/react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Text,
} from "@chakra-ui/react";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";

function CreateUser() {
  const { t } = useTranslation();
  const [last_name, setLastName] = useState("");
  const [first_name, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branch_id, setBranchId] = useState("");
  const [currentUserRoleId, setCurrentUserRoleId] = useState(null);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const pageId = 17;

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/roles?page_id=${pageId}`
        );
        if (response) {
          setRoles(response.data);
        } else {
          console.error("Error fetching roles");
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };
    fetchRoles();

    // Get current user role from sessionStorage (if available)
    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      setCurrentUserRoleId(user?.role_id || null);
    } catch {}

    // Fetch branches for branch selection
    const fetchBranches = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/branches?page_id=${pageId}`
        );
        if (response) {
          setBranches(response.data);
        }
      } catch (error) {
        // Ignore branch fetch errors
      }
    };
    fetchBranches();
  }, []);

  // Validate password: 7-16 chars, at least one uppercase letter, one digit and one special character.
  const validatePassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{7,16}$/;
    return regex.test(password);
  };

  // Validate email using a simple regex.
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Validate the entire form and return a specific error message, if any.
  const validateForm = () => {
    if (!first_name.trim()) {
      return t("First name is required");
    }
    if (!last_name.trim()) {
      return t("Last name is required");
    }
    if (!email.trim()) {
      return t("Email is required");
    }
    if (!validateEmail(email)) {
      return t("Please enter a valid email address");
    }
    if (!password) {
      return t("Password is required");
    }
    if (!validatePassword(password)) {
      return t("Password must be 7-16 characters with at least one uppercase letter, one number, and one special character");
    }
    if (!roleId) {
      return t("Role selection is required");
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Run form validations.
    const errorMsg = validateForm();
    if (errorMsg) {
      setError(errorMsg);
      return;
    }

    try {
      const data = {
        first_name,
        last_name,
        email,
        password,
        role_id: parseInt(roleId, 10),
      };
      if (currentUserRoleId === 3 && roleId === "5") {
        data.branch_id = branch_id;
      }
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/user/register?page_id=${pageId}`,
        "POST",
        data
      );
      if (response?.status === "success") {
        window.location.reload();
      } else {
        setError(response?.message || t("Failed to create user"));
      }
    } catch (error) {
      console.error("Error creating user:", error);
      setError(t("Failed to create user. Please try again later."));
    }
  };

  return (
    <FormControl>
      <form onSubmit={handleSubmit}>
        <FormLabel fontSize="sm" fontWeight="500" mb="8px" color="blue.600">
          {t("First Name")}
        </FormLabel>
        <Input
          isRequired
          type="text"
          placeholder="First Name"
          mb="24px"
          size="lg"
          value={first_name}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <FormLabel fontSize="sm" fontWeight="500" mb="8px" color="blue.600">
          {t("Last Name")}
        </FormLabel>
        <Input
          isRequired
          type="text"
          placeholder="Last Name"
          mb="24px"
          size="lg"
          value={last_name}
          onChange={(e) => setLastName(e.target.value)}
        />
        <FormLabel fontSize="sm" fontWeight="500" mb="8px" color="blue.600">
          {t("Email")}
        </FormLabel>
        <Input
          isRequired
          type="email"
          placeholder="Email"
          mb="24px"
          size="lg"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
       <FormControl  fontSize="sm" fontWeight="500" mb="8px" color="blue.600">
          {t("Password")}
        </FormControl>
        <InputGroup mb="24px" size="lg">
          <Input
            isRequired
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            pr="40px"
          />
          <InputRightElement width="3rem">
            <Button
              h="1.75rem"
              size="sm"
              variant="ghost"
              onClick={() => setShowPassword((show) => !show)}
              tabIndex={-1}
            >
              {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
            </Button>
          </InputRightElement>
        </InputGroup>
        <FormLabel fontSize="sm" fontWeight="500" mb="8px" color="blue.600">
          {t("Role")}
        </FormLabel>
        <Select
          isRequired
          placeholder={t("Select Role")}
          mb="24px"
          size="lg"
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
        >
          {roles.map((role) => (
            <option key={role.role_id} value={role.role_id}>
              {role.role_name}
            </option>
          ))}
        </Select>
        {currentUserRoleId === 3 && roleId === "5" && (
          <>
            <FormLabel fontSize="sm" fontWeight="500" mb="8px" color="blue.600">
              {t("Branch")}
            </FormLabel>
            <Select
              isRequired
              placeholder="Select Branch"
              mb="24px"
              size="lg"
              value={branch_id}
              onChange={(e) => setBranchId(e.target.value)}
            >
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </>
        )}
        <Button
          type="submit"
          fontSize="sm"
          colorScheme="cyan"
          fontWeight="500"
          w="100%"
          h="50px"
          mb="24px"
          color="white"
        >
          {t("Create User")}
        </Button>
        {error && (
          <Box color="red" mt={2}>
            {Array.isArray(error)
              ? error.map((line, i) => <div key={i}>{line}</div>)
              : <div>{error}</div>
            }
          </Box>
        )}
      </form>
    </FormControl>
  );
}

export default CreateUser;
